import React, { useEffect, useMemo, useState } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { auth, app, API_BASE_URL } from '../../firebase/firebaseConfig';
import ErrorModal from '../ui/ErrorModal';
import {
  AccountPortalShell,
  DetailShell,
  HCard,
  HCardButton,
  HearthIcon,
  SerifHeading,
  SectionLabel,
  Lede,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HButtonPair,
} from '../h';
import {
  ShippingAddressForm,
  emptyAddress,
  isAddressFilled,
  summariseAddress,
} from './ShippingAddressForm';
import {
  getCountryByCode,
  getStateConfig,
  validatePostcode,
} from '../../utils/shippingCountries';
import { cn } from '../../lib/utils';

/* What's missing from an incomplete address, for the error modal. */
function incompleteMessage(a) {
  const hasBasics = !!(a && a.name && a.line1 && a.city && a.postalCode && a.country);
  if (hasBasics) {
    // Only the state can be missing: isAddressFilled also requires it for US/CA.
    return `Please choose a ${getStateConfig(a.country).label.toLowerCase()}.`;
  }
  const stateCfg = getStateConfig(a?.country);
  const state = stateCfg.required ? `, ${stateCfg.label.toLowerCase()}` : '';
  return `Please complete recipient name, country, address, city${state} and postcode.`;
}

/* v9 mockups 1.3 / 1.4 / 1.5 — Confirm shipping addresses.

   Behaviour preserved:
   - numOfFamilies-many addresses, index 0 pre-populated from order.
   - Submit POSTs /user/confirmShippingAddresses with { email, shippingAddresses }.
   - Postcode validated per-country before submit.
   - State/province required (from a fixed list) for US and Canadian
     addresses; optional elsewhere.

   New behaviour:
   - Two visual modes for >1 Hearths:
       - "Different addresses" (default) — per-Hearth summary cards.
         Empty cards render in warn (yellow); tapping one opens the edit sub-view.
       - "Same address" — single inline form; on submit, that address is
         duplicated for all Hearths.
   - Edit-one Hearth sub-view (1.5) is a separate full-screen form with its
     own back button. Cancel discards the draft; Save validates and writes back.
*/
function ShippingAddressConfirmation({
  email,
  numOfFamilies,
  shippingAddressAtOrder,
  shippingCountryAtOrder,
  onComplete,
}) {
  const isMulti = numOfFamilies > 1;
  const initialCountry = shippingCountryAtOrder || 'GB';

  const [mode, setMode] = useState(isMulti ? 'different' : 'same');
  const [addresses, setAddresses] = useState(() =>
    Array.from({ length: numOfFamilies }, (_, i) => {
      if (i === 0 && shippingAddressAtOrder) {
        return {
          ...emptyAddress(initialCountry),
          ...shippingAddressAtOrder,
          country: shippingAddressAtOrder.country || initialCountry,
        };
      }
      return emptyAddress(initialCountry);
    }),
  );
  const [sameAddress, setSameAddress] = useState(() => {
    if (shippingAddressAtOrder) {
      return {
        ...emptyAddress(initialCountry),
        ...shippingAddressAtOrder,
        country: shippingAddressAtOrder.country || initialCountry,
      };
    }
    return emptyAddress(initialCountry);
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);

  /* If parent props change after initial mount (e.g. delayed userinfo response),
     re-seed the addresses array with the new defaults. */
  useEffect(() => {
    setAddresses((prev) => {
      // Only resize/refill when the count actually changed; avoid clobbering
      // unsaved edits the user made on screen.
      if (prev.length === numOfFamilies) return prev;
      return Array.from({ length: numOfFamilies }, (_, i) => {
        if (i === 0 && shippingAddressAtOrder) {
          return {
            ...emptyAddress(initialCountry),
            ...shippingAddressAtOrder,
            country: shippingAddressAtOrder.country || initialCountry,
          };
        }
        return emptyAddress(initialCountry);
      });
    });
  }, [numOfFamilies, shippingAddressAtOrder, initialCountry]);

  const handleSignOut = async () => {
    try {
      await signOut(getAuth(app));
      window.location.reload();
    } catch (err) {
      console.error('sign out error', err);
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingDraft(addresses[index] || emptyAddress(initialCountry));
  };
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingDraft(null);
  };
  const saveEdit = () => {
    const draft = editingDraft;
    if (!isAddressFilled(draft)) {
      setErrorMessage(incompleteMessage(draft));
      setShowErrorModal(true);
      return;
    }
    if (!validatePostcode(draft.postalCode, draft.country)) {
      const c = getCountryByCode(draft.country);
      setErrorMessage(`Invalid ${c?.postcodeLabel || 'postcode'} format.`);
      setShowErrorModal(true);
      return;
    }
    setAddresses((prev) => {
      const next = [...prev];
      next[editingIndex] = draft;
      return next;
    });
    setEditingIndex(null);
    setEditingDraft(null);
  };

  const allDifferentFilled = useMemo(
    () => addresses.every(isAddressFilled),
    [addresses],
  );
  const sameFilled = isAddressFilled(sameAddress);
  const canSubmit = mode === 'same' || !isMulti ? sameFilled : allDifferentFilled;

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const list =
        mode === 'same' || !isMulti
          ? Array.from({ length: numOfFamilies }, () => ({ ...sameAddress }))
          : addresses;

      for (const a of list) {
        if (!isAddressFilled(a)) {
          setErrorMessage(incompleteMessage(a));
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }
        if (!validatePostcode(a.postalCode, a.country)) {
          const c = getCountryByCode(a.country);
          setErrorMessage(`Invalid ${c?.postcodeLabel || 'postcode'} format.`);
          setShowErrorModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/confirmShippingAddresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email,
          shippingAddresses: list.map((a, i) => ({ ...a, hearthIndex: i })),
        }),
      });
      const data = await response.json();
      if (data.success) {
        // Pass the just-confirmed addresses up so the thank-you page can show
        // every Hearth's destination (not just the order-time fallback).
        onComplete(list);
      } else {
        setErrorMessage(data.error || 'Failed to save shipping addresses.');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('submit error', err);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ──────────────── Edit-one sub-view (v9 1.5) ──────────────── */
  if (editingIndex !== null) {
    const isPrimary = editingIndex === 0;
    return (
      <DetailShell
        onBack={cancelEdit}
        breadcrumb={
          <span className="text-[13px] text-slate-500">
            <SectionLabel as="span" className="text-[10px] tracking-[0.16em] block">
              Editing address
            </SectionLabel>
            <span className="text-[14px] font-semibold text-slate-900">
              Hearth {editingIndex + 1}
            </span>
          </span>
        }
      >
        <div className="px-4 pb-6">
          <div className="h-px bg-slate-200 -mx-5 mb-4" />
          {!isPrimary && (
            <HCard variant="muted" className="p-4 mb-4 flex items-start gap-3">
              <HearthIcon size="sm" className="mt-0.5" />
              <div className="flex-1 text-[12px] text-slate-600 leading-relaxed">
                This Hearth is going to a different home. Give us the name and
                full address of whoever it&rsquo;s going to.
              </div>
            </HCard>
          )}
          <SectionLabel className="mb-1.5">Address</SectionLabel>
          <ShippingAddressForm
            idPrefix={`edit-${editingIndex}`}
            value={editingDraft}
            onChange={setEditingDraft}
            autoFocus
          />
          <HButtonPair className="mt-5">
            <HSecondaryButton onClick={cancelEdit}>Cancel</HSecondaryButton>
            <HPrimaryButton onClick={saveEdit}>Save address</HPrimaryButton>
          </HButtonPair>
        </div>
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Address error"
          onClose={() => setShowErrorModal(false)}
        />
      </DetailShell>
    );
  }

  /* ──────────────── Main list / single view (v9 1.3 / 1.4) ──────────────── */
  // Mockup 1.3/1.4 shows only the "ACCOUNT PORTAL" caps label top-right; the
  // Sign-out action is already in the bottom HButtonPair, so don't duplicate.
  return (
    <AccountPortalShell>
      <div className="px-4 pt-3 pb-6">
        <SectionLabel className="mb-2">Before we ship</SectionLabel>
        <SerifHeading size="lg" className="mb-2">
          {isMulti ? 'Confirm your Shipping Addresses' : 'Confirm your Shipping Address'}
        </SerifHeading>
        <Lede className="mb-4">
          {!isMulti
            ? 'Tell us where to ship your Hearth. We currently ship to the UK, EU, US and Canada.'
            : mode === 'different'
              ? `Tell us where to send your ${numOfFamilies} Hearths to. They can go to different addresses. We currently ship to the UK, EU, US and Canada.`
              : `All ${numOfFamilies} Hearths will be shipped to the same address. We currently ship to the UK, EU, US and Canada.`}
        </Lede>

        {isMulti && (
          <div className="rounded-2xl bg-slate-100 p-1 mb-4 grid grid-cols-2 gap-1">
            {[
              { id: 'different', label: 'Different addresses' },
              { id: 'same', label: 'Same address' },
            ].map((tab) => {
              const active = mode === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setMode(tab.id)}
                  className={cn(
                    'text-[12px] py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900',
                    active
                      ? 'bg-white text-slate-900 font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
                      : 'text-slate-500 font-medium hover:text-slate-700',
                  )}
                  aria-pressed={active}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {(!isMulti || mode === 'same') && (
          <>
            <SectionLabel className="mb-2">Destination address</SectionLabel>
            <ShippingAddressForm
              idPrefix="same"
              value={sameAddress}
              onChange={setSameAddress}
            />
          </>
        )}

        {isMulti && mode === 'different' && (
          <div className="space-y-3">
            {addresses.map((address, idx) => {
              const filled = isAddressFilled(address);
              return (
                <HCardButton
                  key={idx}
                  variant={filled ? 'default' : 'warn'}
                  onClick={() => startEdit(idx)}
                  className="p-4"
                >
                  <div className="flex items-start gap-3">
                    <HearthIcon size="sm" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-slate-900 mb-1">
                        Hearth {idx + 1}
                      </div>
                      {filled ? (
                        <>
                          <div className="text-[12px] text-slate-700 leading-relaxed">
                            {address.name} · {summariseAddress(address)}
                          </div>
                          <div className="text-[11px] font-semibold text-slate-900 mt-1">
                            Tap to edit →
                          </div>
                        </>
                      ) : (
                        <div className="text-[12px] text-amber-800 leading-relaxed">
                          Address not set yet — tap to add.
                        </div>
                      )}
                    </div>
                  </div>
                </HCardButton>
              );
            })}
          </div>
        )}

        <HButtonPair className="mt-6">
          <HSecondaryButton onClick={handleSignOut}>Sign out</HSecondaryButton>
          <HPrimaryButton onClick={submit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting
              ? 'Confirming…'
              : !isMulti || mode === 'same'
                ? 'Confirm address'
                : numOfFamilies === 2
                  ? 'Confirm both'
                  : `Confirm all ${numOfFamilies}`}
          </HPrimaryButton>
        </HButtonPair>

        {!canSubmit && isMulti && mode === 'different' && (
          <p className="text-[11px] text-slate-400 mt-3 text-center">
            Add {numOfFamilies === 2 ? 'both' : 'all'} addresses to continue.
          </p>
        )}
      </div>

      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        title="Shipping address error"
        onClose={() => setShowErrorModal(false)}
      />
    </AccountPortalShell>
  );
}

export default ShippingAddressConfirmation;
